import React from 'react';
import { Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    container: {
        marginTop: 10,
    },
    titleSection: {
        textAlign: 'center',
        marginBottom: 10,
        padding: 6,
        backgroundColor: '#f0f7ff',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#2e7d32',
        borderRadius: 4,
    },
    mainTitle: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: '#2e7d32',
        marginBottom: 2,
    },
    subtitle: {
        fontSize: 10,
        fontFamily: 'Helvetica',
        color: '#495057',
    },
    termsSection: {
        marginTop: 0,
        padding: 8,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dee2e6',
        borderRadius: 3,
    },
    termsTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 2,
        color: '#2e7d32',
        textAlign: 'center',
    },
    termsText: {
        fontSize: 7,
        fontFamily: 'Helvetica',
        lineHeight: 1,
        color: '#495057',
        textAlign: 'justify',
        marginBottom: 4,
    },
    signatureSection: {
        marginTop: 5,
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    signatureBox: {
        width: '45%',
        padding: 10,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#ffffff',
    },
    signatureLabel: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 5,
        textAlign: 'center',
        color: '#495057',
    },
    signatureLine: {
        borderBottom: 1,
        borderBottomColor: '#000000',
        marginBottom: 5,
        height: 40,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    signatureImage: {
        maxWidth: '80%',
        maxHeight: 35,
        objectFit: 'contain',
    },
    dateLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        textAlign: 'center',
        color: '#495057',
    },
    totalsSection: {
        marginTop: 10,
        padding: 8,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dee2e6',
        borderRadius: 3,
    },
    totalsTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        color: '#495057',
        textAlign: 'center',
    },
    totalsGrid: {
        flexDirection: 'row',
        justifyContent: 'space-around',
    },
    totalColumn: {
        textAlign: 'center',
    },
    totalLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#6c757d',
        marginBottom: 2,
    },
    totalValue: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
        borderBottom: 1,
        borderBottomColor: '#6c757d',
        paddingBottom: 1,
        paddingLeft: 6,
        paddingRight: 6,
    },
    strong: {
        fontFamily: 'Helvetica-Bold',
        fontWeight: 'bold',
    },
    disinfectantSection: {
        marginTop: 4,
        marginBottom: 4,
        padding: 6,
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dee2e6',
        borderRadius: 2,
    },
    disinfectantTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 4,
        color: '#495057',
        textAlign: 'center',
    },
    disinfectantText: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#6c757d',
    },
    disinfectantValue: {
        fontSize: 9,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
    },
    productsSection: {
        marginTop: 10,
        padding: 10,
        backgroundColor: '#fff8e1',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#2e7d32',
        borderRadius: 5,
    },
    productsSectionTitle: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 8,
        color: '#2e7d32',
        textAlign: 'center',
    },
    productsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: 8,
    },
    productItem: {
        width: '30%',
        minWidth: '30%',
        padding: 8,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
        textAlign: 'center',
    },
    productName: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#2e7d32',
        marginBottom: 4,
        textAlign: 'center',
    },
    productRate: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#495057',
        textAlign: 'center',
    }
});

interface ChemAppsAgreementsSectionProps {
    agreement: any;
    sheetType: 'chemApps' | 'disinfectant' | 'sprayBar';
    agreementTitle: string;
    customerSignature?: any; // From imageStore.signature
}

const ChemAppsAgreementsSection: React.FC<ChemAppsAgreementsSectionProps> = ({
    agreement,
    sheetType,
    agreementTitle,
    customerSignature
}) => {
    // Standard terms and conditions text
    const getTermsText = () => {
        return [
            "Customer and/or storer agrees that they have been warned that CIPC Potato Sprout Inhibitor should not be used on, around, or in the near proximity of potatoes that may be used as seed. Customer and/or storer does hereby release Agri-stor Company from any liability for any damage to seed potatoes stored against this warning.",
            "Customer and/or storer agrees that there is a risk of fire during the application. An Aerosol Potato Sprout Inhibitor or Disinfectant will not cause a fire; however, there is a risk of fire damage during application not related to the product nor related to the method of application but generally due to methods and materials of construction of the storage, including but not limited to wiring and insulation.",
            "Customer and / or storer acknowledges that potatoes may deteriorate or be otherwise damaged by bacteria, fungi, disease, insects, aging effects such as internal sprouting, environmental factors(humidity, temperature, and air flow), and other causes, which may occur regardless of whether an Aerosol Potato Sprout Inhibitor or Disinfectant is or is not applied to the potatoes.",
            "Customer and / or storer acknowledges that the degree of sprout control or disinfection is affected by the physical condition of the potatoes, varietal characteristics, the air handling system and storage layout, environmental factors, the time not within the control of the applicator, including but not limited to electrical and / or other application equipment which may be provided by Agri - Stor Company at the customer's request which may or may not be adequate for the application of Aerosol Potato Sprout Inhibitors or Disinfectants.",
            "Agri - Stor Company has warranted only that the product shall conform to the manufacturer's specifications according to the manufacturer's recommendations and has made no other representations or warranties, expressed or implied, and has not warranted that the product would be merchantable or that it would be suitable for any particular purpose, and disclaims any warranty of and liability for economic damage, including loss of profit.",
            "Agri - Stor Company is applying Aerosol Potato Sprout Inhibitor(s) according to the label and in accordance with applicable U.S.and state laws and regulations.Agri - Stor Company, however, does not provide export consulting services and does not make any attempt to ensure compliance with any foreign laws or regulations, including but not limited to foreign maximum residue limits(\"MRL\") regulations associated with any particular pesticide.Thus, to the extent customer and / or storer intends to export its stored potatoes or other crops, customer and / or storer is advised to consult with appropriate third parties to ensure compliance with any foreign laws or regulations, including but not limited to foreign MRL regulations. Customer and / or storer is solely responsible for ensuring compliance with applicable foreign laws or regulations as they may apply to exported potatoes or other crops.",
            "Customer and / or storer also agrees to release Agri - Stor Company, their respective successors, assigns, agents, representatives, and employees and to save and hold harmless the same from and against any and all suits or claims, in law or in equity, that may be alleged to have occurred in the course of the performance of this agreement by the applicator.",
            "Customer and / or storer, if not the owner of the aforesaid warehouse(s), shall advise said owner(s) of this notice prior to application.",
            "Agri - Stor Company will assume NO liability if: the potatoes have had water and / or other substances applied to them either before or after application and / or if there is excessive sprouting(1 / 2\" or greater) on potatoes.",
            "Payment shall be due to AGRI - STOR COMPANY according to the terms listed in the Invoice. All accounts not paid by the due date shall be charged a monthly finance charge of 1 - 1 / 2 % of the unpaid balance per month.This is an annual percentage rate of 18 % unless otherwise stated herein.In the event of non - payment by the customer and / or storer of any of the sums due under agreement, the customer and / or storer hereby agrees that in the event this agreement is placed with an attorney for collection or if suit is instituted for its collection, the customer and / or storer agrees to pay, in either case, reasonable attorney fees, together with the costs of the suit. Customer and / or storer agrees that if the full amount due is not paid within 30 days, Agri - Stor Company may file a lien against customer and / or storer's potatoes."
        ];
    };

    return (
        <View style={styles.container}>
            {/* Main Title Section */}
            <View style={styles.titleSection}>
                <Text style={styles.mainTitle}>{agreementTitle}</Text>
                <Text style={styles.subtitle}>Service Agreement and Specifications</Text>
            </View>

            {/* Terms and Conditions Section */}
            <View style={styles.termsSection}>
                <Text style={styles.termsTitle}>Terms and Conditions</Text>
                {getTermsText().map((term, index) => {
                    // Special handling for terms that need bold text
                    if (index === 5) { // Term 6 - export compliance
                        return (
                            <Text key={index} style={styles.termsText}>
                                {index + 1}. Agri - Stor Company is applying Aerosol Potato Sprout Inhibitor(s) according to the label and in accordance with applicable U.S.and state laws and regulations.Agri - Stor Company, however, does not provide export consulting services and does not make any attempt to ensure compliance with any foreign laws or regulations, including but not limited to foreign maximum residue limits("MRL") regulations associated with any particular pesticide.Thus, to the extent customer and / or storer intends to export its stored potatoes or other crops, customer and / or storer is advised to consult with appropriate third parties to ensure compliance with any foreign laws or regulations, including but not limited to foreign MRL regulations. <Text style={styles.strong}>Customer and / or storer is solely responsible for ensuring compliance with applicable foreign laws or regulations as they may apply to exported potatoes or other crops.</Text>
                            </Text>
                        );
                    } else if (index === 8) { // Term 9 - NO liability
                        return (
                            <Text key={index} style={styles.termsText}>
                                {index + 1}. Agri - Stor Company will assume <Text style={styles.strong}>NO</Text> liability if: the potatoes have had water and / or other substances applied to them either before or after application and / or if there is excessive sprouting(1 / 2" or greater) on potatoes.
                            </Text>
                        );
                    } else if (index === 9) { // Term 10 - Payment and lien
                        return (
                            <Text key={index} style={styles.termsText}>
                                {index + 1}. Payment shall be due to AGRI - STOR COMPANY according to the terms listed in the Invoice. All accounts not paid by the due date shall be charged a monthly finance charge of 1 - 1 / 2 % of the unpaid balance per month.This is an annual percentage rate of 18 % unless otherwise stated herein.In the event of non - payment by the customer and / or storer of any of the sums due under agreement, the customer and / or storer hereby agrees that in the event this agreement is placed with an attorney for collection or if suit is instituted for its collection, the customer and / or storer agrees to pay, in either case, reasonable attorney fees, together with the costs of the suit. <Text style={styles.strong}>Customer and / or storer agrees that if the full amount due is not paid within 30 days, Agri - Stor Company may file a lien against customer and / or storer's potatoes.</Text>
                            </Text>
                        );
                    } else {
                        return (
                            <Text key={index} style={styles.termsText}>
                                {index + 1}. {term}
                            </Text>
                        );
                    }
                })}

                <Text style={styles.termsText}>
                    11. Customer and/or storer agrees that the Aerosol Potato Sprout Inhibitor(s) or Disinfectant(s) will be applied at the rate(s) noted below.
                </Text>

                {sheetType === 'disinfectant' && (
                    <View style={styles.disinfectantSection}>
                        <Text style={styles.disinfectantTitle}>
                            COST PER MAN HOUR: (Ventilation Pipe, Equipment, or Truck Beds, etc.)
                            <Text> </Text>
                            <Text style={styles.disinfectantText}>
                                Cost per Hour: <Text style={styles.disinfectantValue}>${agreement.disinfectantCost} </Text>
                            </Text>
                            <Text style={styles.disinfectantText}>
                                Hours: <Text style={styles.disinfectantValue}>{agreement.disinfectantHours} </Text>
                            </Text>
                        </Text>
                    </View>
                )}

                {/* Agreement Totals Section */}
                <View style={styles.totalsSection}>
                    <Text style={styles.totalsTitle}>IN CONSIDERATION OF THE APPLICATION, CUSTOMER AND/OR STORER AGREES TO PAY AGRI-STOR COMPANY AS FOLLOWS:</Text>
                    <View style={styles.totalsGrid}>
                        <View style={styles.totalColumn}>
                            <Text style={styles.totalLabel}>AGREED AMOUNT:</Text>
                            <Text style={styles.totalValue}>${agreement.amount}</Text>
                        </View>
                        <View style={styles.totalColumn}>
                            <Text style={styles.totalLabel}>AGREED CWT/TONS</Text>
                            <Text style={styles.totalValue}>{agreement.cwt1}</Text>
                        </View>
                        <View style={styles.totalColumn}>
                            <Text style={styles.totalLabel}> </Text>
                            <Text style={styles.totalValue}>${agreement.cwt2}</Text>
                        </View>
                    </View>
                </View>
            </View>

            {/* Product Section */}
            {(agreement.product1 || agreement.product2 || agreement.product3) && (
                <View style={styles.productsSection}>
                    <View style={styles.productsGrid}>
                        {agreement.product1 && (
                            <View style={styles.productItem}>
                                <Text style={styles.productName}>{agreement.product1}</Text>
                                <Text style={styles.productRate}>Rate: {agreement.rate1}</Text>
                            </View>
                        )}
                        {agreement.product2 && (
                            <View style={styles.productItem}>
                                <Text style={styles.productName}>{agreement.product2}</Text>
                                <Text style={styles.productRate}>Rate: {agreement.rate2}</Text>
                            </View>
                        )}
                        {agreement.product3 && (
                            <View style={styles.productItem}>
                                <Text style={styles.productName}>{agreement.product3}</Text>
                                <Text style={styles.productRate}>Rate: {agreement.rate3}</Text>
                            </View>
                        )}
                    </View>
                </View>
            )}

            {/* Signature Section */}
            <View style={styles.signatureSection}>
                <View style={styles.signatureBox}>
                    <Text style={styles.signatureLabel}>Customer Signature</Text>
                    <View style={styles.signatureLine}>
                        {customerSignature?.data && (
                            <Image
                                style={styles.signatureImage}
                                src={customerSignature.data}
                            />
                        )}
                    </View>
                    <Text style={styles.dateLabel}>Date: {agreement?.date || ''}</Text>
                </View>

                <View style={styles.signatureBox}>
                    <Text style={styles.signatureLabel}>Service Provider Signature</Text>
                    <View style={styles.signatureLine}>
                        {agreement?.companySign && (
                            <Text style={{
                                fontSize: 12,
                                fontFamily: 'Helvetica',
                                textAlign: 'center',
                                paddingTop: 8,
                                color: '#2e7d32'
                            }}>
                                {agreement.companySign}
                            </Text>
                        )}
                    </View>
                    <Text style={styles.dateLabel}>Date: {agreement?.date || ''}</Text>
                </View>
            </View>
        </View>
    );
};

export default ChemAppsAgreementsSection;
